import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import ThumbnailImage from "../../../core/common/thumbnailImage";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import SubscriptionDetailsModal from "./subscriptionDetailsModal";
import PreLoader from "../../../core/common/preLoader";
import { Subscription } from "../../../core/data/interface";
import useFetch from "../../../hooks/useFetch";
import { API } from "../../../common/Constants";
import { getAPIHeaders } from "../../../common/Utils";
import "./subscriptions.css";
import CopyRightInfo from "../../../core/common/copyRightInfo";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { DEFAULT_THUMBNAIL_URL } from "../../../environment";

// Helper function to count study materials across all courses in a subscription
const countStudyMaterials = (subscription: Subscription) => {
  let totalVideos = 0;
  let totalDocuments = 0;
  let totalLinks = 0;
  
  subscription.courses.forEach(course => {
    course.courseDetails?.subjects?.forEach(subject => {
      subject.syllabus.forEach(syllabus => {
        syllabus.studyMaterials.forEach(material => {
          switch (material.materialType) {
            case 'VIDEO':
              totalVideos++;
              break;
            case 'NOTE':
            case 'FILE':
              totalDocuments++;
              break;
            case 'LINK':
              // Check if it's a YouTube link (including YouTube Shorts)
              if (material.content && (material.content.includes('youtube.com') || material.content.includes('youtu.be') || material.content.includes('m.youtube.com'))) {
                totalVideos++;
              } else {
                totalLinks++;
              }
              break;
          }
        });
      });
    });
  });
  
  return { videos: totalVideos, documents: totalDocuments, links: totalLinks };
};

// Helper function to calculate discount percentage
const calculateDiscountPercentage = (originalPrice: number, promotionalPrice: number) => {
  if (originalPrice <= 0) return 0;
  return Math.round(((originalPrice - promotionalPrice) / originalPrice) * 100);
};

// Helper function to get unique course thumbnails for carousel
const getCourseThumbnails = (subscription: Subscription) => {
  return subscription.courses.map(course => ({
    courseId: course.courseId,
    courseTitle: course.courseTitle,
    thumbnail: course.courseMetadata?.thumbnail_url || DEFAULT_THUMBNAIL_URL
  }));
};

const Subscriptions = () => {
  const navigate = useNavigate();
  const [selectedCategories, setSelectedCategories] = useState<string[]>(["All"]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const [isSelectedSubscriptionEnrolled, setIsSelectedSubscriptionEnrolled] = useState<boolean>(false);
  const [availableSubscriptions, setAvailableSubscriptions] = useState<Subscription[]>([]);
  const [userEnrolledSubscriptions, setUserEnrolledSubscriptions] = useState<Subscription[]>([]);
  const [isMySubscriptionsCollapsed, setIsMySubscriptionsCollapsed] = useState<boolean>(false);
  const [isAvailableSubscriptionsCollapsed, setIsAvailableSubscriptionsCollapsed] = useState<boolean>(false);
  
  // API call setup
  const { isLoading, error, callFetch } = useFetch();

  const fetchSubscriptionData = useCallback(() => {
    callFetch(
      API.SUBCRIPTION_HOME_DATA,
      'GET',
      getAPIHeaders(),
      null,
      null
    ).then((response)=>{
      // Set enrolled batches
      if(response.userSubscriptionInfo?.hasActiveSubscription){
        setUserEnrolledSubscriptions(response.userSubscriptionInfo?.activeSubscriptions);
      } else {
        setUserEnrolledSubscriptions([]);
      }
      
      // Filter available subscriptions to exclude enrolled ones
      const enrolledSubscriptionIds = response.userSubscriptionInfo?.hasActiveSubscription 
        ? response.userSubscriptionInfo.activeSubscriptions.map((sub: any) => sub.subscriptionPlanId)
        : [];
      
      const filteredAvailableSubscriptions = response.availableSubscriptionPlans.filter(
        (subscription: any) => !enrolledSubscriptionIds.includes(subscription.subscriptionPlanId)
      );
      
      setAvailableSubscriptions(filteredAvailableSubscriptions);
      
    }).catch((err) => {
      console.error('Failed to fetch subscriptions:', err);
    });
  }, []);

  useEffect(() => {
    fetchSubscriptionData();
  }, []);


  // Handle API errors
  useEffect(() => {
    if (error) {
      console.error('Error fetching subscriptions:', error);
    }
  }, [error]);

  // Get unique course types across all subscriptions' courses
  const getAllCourseTypes = (): string[] => {
    if (!availableSubscriptions || availableSubscriptions.length === 0) return ["All"];
    const allCourseTypes = availableSubscriptions.flatMap((sub: Subscription) => sub.courses?.map(course => course.courseType) || []);
    const uniqueTypes = Array.from(new Set(allCourseTypes)).filter(Boolean) as string[];
    return ["All", ...uniqueTypes];
  };

  const handleSubscribe = (subscription: Subscription) => {
    navigate(all_routes.order + '?subscriptionPlanId=' + subscription.subscriptionPlanId);
  };

  const categories: string[] = getAllCourseTypes();

  // Handle chip selection
  const handleChipClick = (category: string) => {
    if (category === "All") {
      setSelectedCategories(["All"]);
    } else {
      setSelectedCategories(prev => {
        // Remove "All" if it's selected
        const newCategories = prev.filter(cat => cat !== "All");
        
        // If category is already selected, remove it; otherwise add it
        if (newCategories.includes(category)) {
          const updated = newCategories.filter(cat => cat !== category);
          // If no categories left, select "All"
          return updated.length === 0 ? ["All"] : updated;
        } else {
          return [...newCategories, category];
        }
      });
    }
  };

  // Show error message if API call failed
  if (error && !isLoading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="text-center">
              <i className="ti ti-alert-circle display-1 text-danger mb-3"></i>
              <h4>Error loading batches</h4>
              <p className="text-muted">Failed to load batches. Please try again later.</p>
              <button 
                className="btn btn-primary mt-3"
                onClick={fetchSubscriptionData}
              >
                <i className="ti ti-refresh me-2"></i>
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isLoading && 
    (!availableSubscriptions || availableSubscriptions.length === 0) &&
    !userEnrolledSubscriptions.length) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="text-center">
              <i className="ti ti-book-off display-1 text-muted mb-3"></i>
              <h4>No batches available</h4>
              <p className="text-muted">There are no batches to display at the moment.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const filterSubscriptions = (subscriptions: Subscription[]) => {
    return subscriptions.filter(subscription => {
      // Filter by selected course types
      const categoryMatch = selectedCategories.includes("All") ||
        subscription.courses?.some(course => selectedCategories.includes(course.courseType));

      // Filter by search term (plan name/description)
      const searchMatch = searchTerm === "" || 
        subscription.planName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        subscription.planDescription.toLowerCase().includes(searchTerm.toLowerCase());

      return categoryMatch && searchMatch;
    });
  };

  const filteredSubscriptions = filterSubscriptions(availableSubscriptions || []);

  // Carousel settings for course thumbnails
  const carouselSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: false,
    adaptiveHeight: true
  };

  const renderStars = (rating: number) => {
    const stars = [] as JSX.Element[];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    for (let i = 0; i < fullStars; i++) {
      stars.push(<i key={i} className="fas fa-star text-warning"></i>);
    }
    if (hasHalfStar) {
      stars.push(<i key="half" className="fas fa-star-half-alt text-warning"></i>);
    }
    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<i key={`empty-${i}`} className="far fa-star text-warning"></i>);
    }
    return stars;
  };

  const renderSubscriptionCard = (subscription: Subscription, isEnrolled: boolean = false) => {
    const materials = countStudyMaterials(subscription);
    const thumbnails = getCourseThumbnails(subscription);
    const discountPercentage = calculateDiscountPercentage(subscription.pricing.price, subscription.pricing.promotionalPrice);
    const highestRating = subscription.courses.length
      ? Math.max(...subscription.courses.map(c => c.analytics?.averageRating || 0))
      : 0;
    const totalStudyMaterials = materials.videos + materials.documents + materials.links
    const totalAssessments = subscription.courses.reduce((acc, course) => acc + (course.assessmentCount || 0), 0);
    
    return (
      <div key={subscription.subscriptionPlanId} className="col-lg-3 col-md-4 col-sm-6 col-12 mb-4">
        <div className="card subscription-card h-100">
          {/* Course Thumbnails Carousel */}
          <div className="position-relative">
            {thumbnails.length > 1 ? (
              <Slider {...carouselSettings}>
                {thumbnails.map((thumbnail, index) => (
                  <div key={index}>
                    <ThumbnailImage
                      src={thumbnail.thumbnail}
                      alt={thumbnail.courseTitle}
                      overlayContent={
                        <div className="position-absolute top-0 start-0 end-0 p-2" style={{
                          background: 'linear-gradient(rgba(0,0,0,0.7), transparent)',
                          color: 'white',
                          pointerEvents: 'auto'
                        }}>
                          <div className="small fw-medium text-truncate" title={thumbnail.courseTitle}>
                            {thumbnail.courseTitle}
                          </div>
                        </div>
                      }
                    />
                  </div>
                ))}
              </Slider>
            ) : (
              <ThumbnailImage
                src={thumbnails[0]?.thumbnail || DEFAULT_THUMBNAIL_URL}
                alt={subscription.planName}
                overlayContent={
                  <div className="position-absolute top-0 start-0 end-0 p-2" style={{
                    background: 'linear-gradient(rgba(0,0,0,0.7), transparent)',
                    color: 'white',
                    pointerEvents: 'auto'
                  }}>
                    <div className="small fw-medium text-truncate" title={thumbnails[0]?.courseTitle || subscription.planName}>
                      {thumbnails[0]?.courseTitle || subscription.planName}
                    </div>
                  </div>
                }
              />
            )}
            
            {/* Badges */}
            <div className="position-absolute bottom-0 start-0 p-2">
              <span className="badge bg-primary">{subscription.planType}</span>
            </div>
            <div className="position-absolute bottom-0 end-0 p-2">
              <span className="badge bg-success">{subscription.courses.length} Courses</span>
            </div>
          </div>

          <div className="card-body">
            {/* Subscription Info */}
            <div className="mb-1">
              <h5 className="card-title mb-2">{subscription.planName}</h5>
              <p className="card-text text-muted small mb-2" style={{ 
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}>{subscription.planDescription}</p>

              {/* Highest Rating */}
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="d-flex align-items-center">
                  <div className="me-1">
                    {renderStars(highestRating)}
                  </div>
                  <span className="small fw-medium">{highestRating.toFixed(1)}</span>
                </div>
              </div>

              {/* Study Materials Count */}
              <div className="d-flex justify-content-between small text-muted mb-2">
                {totalStudyMaterials > 0 && <span>{totalStudyMaterials} Study Materials</span>}
                {totalAssessments > 0 && <span>{totalAssessments} Assessments</span>}
              </div>
            </div>

            {/* Fees */}
            <div>
              <div className="p-3 bg-light rounded">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex flex-column">
                    <div className="small text-muted">
                      {subscription.duration ? `${subscription.duration} month(s)` : 'Lifetime access'}
                    </div>
                  </div>
                  <div className="text-end">
                    <div className="d-flex align-items-center">
                      {subscription.pricing.price > 0 && (
                        <span className="text-decoration-line-through text-muted me-2 small">
                          {subscription.pricing.currencyCode} {subscription.pricing.price}
                        </span>
                      )}
                      <span className="h5 fw-bold text-primary mb-0">
                        {subscription.pricing.currencyCode} {subscription.pricing.promotionalPrice}
                      </span>
                    </div>
                    {discountPercentage > 0 && (
                      <div className="small text-success">
                        {discountPercentage}% OFF
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="card-buttons d-grid gap-2">
              {isEnrolled ? (
                <></>
              ) : (
                <button 
                  className="btn btn-primary"
                  onClick={() => handleSubscribe(subscription)}
                >
                  <i className="ti ti-shopping-cart me-2"></i>
                  Enroll Now
                </button>
              )}
              <button 
                className="btn btn-outline-primary"
                onClick={() => {
                  setSelectedSubscription(subscription);
                  setIsSelectedSubscriptionEnrolled(isEnrolled);
                }}
              >
                <i className="ti ti-eye me-2"></i>
                View Details
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Show loading state
  if (isLoading) {
    return (
      <>
        <PreLoader showLoader={true} />
        <div className="page-wrapper">
          <div className="content">
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
              <div className="text-center">
                <p className="text-muted">Loading batches...</p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }


  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Batches</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.index}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Pages</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Batches
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}

          {/* Search and Filters */}
          <div className="card mb-3">
            <div className="card-body py-3">
              {/* Search Bar */}
              <div className="mb-3">
                <div className="input-group">
                  <input 
                    type="text" 
                    className="form-control" 
                    placeholder="Search batches by name or description..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <button className="btn btn-primary" type="button">
                    <i className="ti ti-search"></i>
                  </button>
                </div>
              </div>

              {/* Category Chips */}
              <div>
                <div className="d-flex flex-wrap justify-content-center gap-2">
                  {categories.map(category => (
                    <button
                      key={category}
                      className={`btn badge ${
                        selectedCategories.includes(category) 
                          ? 'bg-primary' 
                          : 'bg-secondary'
                      } border-0 px-3 py-1`}
                      onClick={() => handleChipClick(category)}
                      style={{ cursor: 'pointer' }}
                    >
                      {category === 'All' ? 'All' : category}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* My Batches Section */}
          {userEnrolledSubscriptions.length > 0 && (
            <>
              <div className="mb-4">
                <h4 
                  className="mb-3 d-flex align-items-center"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setIsMySubscriptionsCollapsed(!isMySubscriptionsCollapsed)}
                >
                  <i className={`ti ti-chevron-${isMySubscriptionsCollapsed ? 'right' : 'down'} me-2`}></i>
                  <i className="ti ti-user-check me-2 text-success"></i>
                  My Batches
                  <span className="badge bg-success ms-2">{userEnrolledSubscriptions.length}</span>
                </h4>
                <div style={{ borderTop: '1px solid #dee2e6', marginTop: '0' }}></div>
              </div>
              {!isMySubscriptionsCollapsed && (
                <div className="row mb-5">
                  {userEnrolledSubscriptions.map((subscription) => renderSubscriptionCard(subscription, true))}
                </div>
              )}
            </>
          )}

          {/* Available Subscriptions Section */}
          <div className="mb-4">
            <h4 
              className="mb-3 d-flex align-items-center"
              style={{ cursor: 'pointer' }}
              onClick={() => setIsAvailableSubscriptionsCollapsed(!isAvailableSubscriptionsCollapsed)}
            >
              <i className={`ti ti-chevron-${isAvailableSubscriptionsCollapsed ? 'right' : 'down'} me-2`}></i>
              <i className="ti ti-crown me-2 text-warning"></i>
              Available Batches
              <span className="badge bg-warning ms-2">{filteredSubscriptions.length}</span>
            </h4>
            <div style={{ borderTop: '1px solid #dee2e6', marginTop: '0' }}></div>
          </div>
          {!isAvailableSubscriptionsCollapsed && (
            <>
              <div className="row">
                {filteredSubscriptions.map((subscription) => renderSubscriptionCard(subscription, false))}
              </div>

              {/* No Results */}
              {filteredSubscriptions.length === 0 && (
                <div className="text-center py-5">
                  <i className="ti ti-search-off display-1 text-muted"></i>
                  <h4 className="mt-3">No available batches found</h4>
                  <p className="text-muted">Try adjusting your filters to find more batches.</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="footer d-sm-flex align-items-center justify-content-center border-top bg-white p-3">
         <CopyRightInfo/>
        </div>
      </div>
      {/* /Page Wrapper */}

      {/* Subscription Details Modal */}
      <SubscriptionDetailsModal 
        subscription={selectedSubscription} 
        onClose={() => {
          setSelectedSubscription(null);
          setIsSelectedSubscriptionEnrolled(false);
        }}
        isEnrolled={isSelectedSubscriptionEnrolled}
      />
    </>
  );
};

export default Subscriptions;
